import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavBar.css";
import "./QrTypeList.css";
import RenderComponent, { RenderContext } from "../RenderComponent/RenderComponent";
import { Popup } from "../PopUp/Popup";
import { removeAuthCookie } from "../../Helpers/cookie/cookieUtils";
import { PUBLIC_URL } from "../../Helpers/config/config";

// --- Interfaces & Types ---
export interface NavBarProps {
  children?: React.ReactNode;
}

interface User {
  role?: string;
  [key: string]: unknown;
}

interface SubMenuItem {
  id: string;
  name: string;
  url: string;
}

interface NavItem {
  id: string;
  name: string;
  url?: string;
  icon: React.ReactNode;
  subItems?: SubMenuItem[];
  matchPaths: string[];
}

interface QuickActionItem {
  name: string;
  url: string;
  image: string;
  shortcut?: string;
}

// --- Icons ---
const UsersIcon = () => (
  <svg viewBox="0 0 20 20" height="20" width="20" aria-hidden="true" fill="currentColor">
    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.04-.3.07-.6.07-.9 0-2.48-1.53-4.57-3.66-5.36a5.002 5.002 0 016.16 4.96 4.99 4.99 0 01-2.57 1.3zM1.07 15.42A4.99 4.99 0 0110 13a4.99 4.99 0 018.93 2.42 1 1 0 01-1 1.08H2.07a1 1 0 01-1-1.08z" />
  </svg>
);

const CanIcon = () => (
  <svg viewBox="0 0 20 20" height="20" width="20" aria-hidden="true" fill="currentColor">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A1 1 0 0111 2.414l5.586 5.586A1 1 0 0117 8.757V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);

const CareerIcon = () => (
  <svg viewBox="0 0 20 20" height="20" width="20" aria-hidden="true" fill="currentColor">
    <path d="M6 6V4a2 2 0 012-2h4a2 2 0 012 2v2h4a1 1 0 011 1v9a2 2 0 01-2 2H3a2 2 0 01-2-2V7a1 1 0 011-1h4zm2-2v2h4V4H8z" />
  </svg>
);

const ChevronIcon = ({ isOpen, isActive }: { isOpen: boolean; isActive: boolean }) => (
  <svg
    className={`chevron ${isOpen ? "open" : ""} ${isActive ? "chevron-active" : ""}`}
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const HamburgerIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" height="20" width="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

// --- Constants & Config ---
const QUICK_ACTION_ITEMS: QuickActionItem[] = [
  { name: 'Shorten / QR Code', url: '/createQr/new', image: 'https://cdn-icons-png.flaticon.com/512/565/565547.png' },
  { name: 'Create V-Card / Landing Page', url: '/createVCard/new', image: 'https://cdn-icons-png.flaticon.com/512/565/565547.png' },
];

const LeadsIcon = () => (
  <svg viewBox="0 0 20 20" height="20" width="20" aria-hidden="true" fill="currentColor">
    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
  </svg>
);

const NAV_ITEMS: NavItem[] = [
  {
    id: "home-page",
    name: "Home Page",
    url: "/HomePageContent",
    icon: (
      <svg viewBox="0 0 20 20" height="20" width="20" aria-hidden="true" fill="currentColor">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
    ),
    matchPaths: ["/HomePageContent"],
  },
  {
    id: "users",
    name: "Users",
    url: "/UserList",
    icon: <UsersIcon />,
    matchPaths: ["/UserList"],
  },
  {
    id: "universities",
    name: "Universities",
    url: "/UniversityList",
    icon: <CareerIcon />,
    matchPaths: ["/UniversityList", "/UniversityForm"],
  },
  {
    id: "programs",
    name: "Programs",
    url: "/ProgramList",
    icon: <CanIcon />,
    matchPaths: ["/ProgramList", "/ProgramForm"],
  },
  {
    id: "leads",
    name: "Leads",
    url: "/LeadList",
    icon: <LeadsIcon />,
    matchPaths: ["/LeadList"],
  }
];

// --- Helpers ---
const shouldShowBackButton = (pathname: string): boolean => {
  const normalizedPath = pathname.toLowerCase();
  const isHome = ["/home", "/dashboard"].includes(normalizedPath);
  if (isHome) return false;

  const isList = normalizedPath.includes("list");
  const isCandidateList = normalizedPath.includes("candidatelist");

  return !isList || isCandidateList;
};

// --- Component ---
const NavBarComponent: React.FC<NavBarProps> = ({ children }) => {
  const user = useContext<User | null>(RenderContext as React.Context<User | null>);
  const location = useLocation();
  const navigate = useNavigate();

  const [trigger, setTrigger] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    NAV_ITEMS.forEach((item) => {
      if (item.subItems && item.matchPaths.includes(location.pathname)) {
        initial[item.id] = true;
      }
    });
    return initial;
  });

  // Keep submenus expanded if active route matches
  useEffect(() => {
    NAV_ITEMS.forEach((item) => {
      if (item.subItems && item.matchPaths.includes(location.pathname)) {
        setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
      }
    });
  }, [location.pathname]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const toggleSubmenu = (itemId: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleLogout = () => {
    removeAuthCookie();
    setIsSidebarOpen(false);
    navigate("/");
  };

  return (
    <div className="layout">
      {/* Quick Action Popup Modal */}
      <Popup
        class="create-modal"
        heading={<h3 className="popup-title">What do you want to create?</h3>}
        setSearch={setTrigger}
        trigger={trigger}
      >
        <div className="option-grid">
          {QUICK_ACTION_ITEMS.map((item, index) => (
            <Link
              key={index}
              to={item.url}
              className="option-card"
              onClick={() => setTrigger(false)}
            >
              <div className="card-left">
                <div className="icon-box">
                  <img src={item.image} alt={item.name} />
                </div>
                <span className="option-text">{item.name}</span>
              </div>
              {item.shortcut && <span className="shortcut">{item.shortcut}</span>}
            </Link>
          ))}
        </div>
      </Popup>

      {/* Mobile Top Header */}
      <header className="mobile-header">
        <h2 className="logo">
          <img src={`${PUBLIC_URL}/images/logo.svg`} alt="chitkara" width={150} height={50} />
        </h2>
        <button
          className="hamburger"
          onClick={toggleSidebar}
          aria-label="Toggle navigation menu"
          aria-expanded={isSidebarOpen}
        >
          <HamburgerIcon />
        </button>
      </header>

      {/* Backdrop overlay for mobile drawer */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? "show" : ""}`}
        onClick={toggleSidebar}
        aria-hidden="true"
      />

      {/* Sidebar Drawer */}
      <aside className={`sidebar ${isSidebarOpen ? "open" : ""}`} aria-label="Main navigation">
        <div className="sidebar-header">
          <h2 className="logo desktop-logo">
            <img src={`${PUBLIC_URL}/images/logo.svg`} alt="chitkara" width={150} height={50} />
          </h2>
        </div>

        <ul className="menu">
          {NAV_ITEMS.map((item) => {
            const isPathActive = item.matchPaths.includes(location.pathname);
            const isSubOpen = Boolean(openSubmenus[item.id]);

            // Single Link item
            if (!item.subItems) {
              return (
                <li key={item.id}>
                  <Link to={item.url!} onClick={() => setIsSidebarOpen(false)}>
                    <div className={`menu-item ${isPathActive ? "active" : ""}`}>
                      {item.icon}
                      <span>{item.name}</span>
                    </div>
                  </Link>
                </li>
              );
            }

            // Accordion Submenu item
            return (
              <li key={item.id} className="submenu-container">
                <div
                  className="menu-dropdown-header"
                  onClick={() => toggleSubmenu(item.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isSubOpen}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSubmenu(item.id);
                    }
                  }}
                >
                  <div className={`menu-item ${isPathActive ? "active" : ""}`}>
                    {item.icon}
                    <span>{item.name}</span>
                    <ChevronIcon isOpen={isSubOpen} isActive={isPathActive} />
                  </div>
                </div>

                {isSubOpen && (
                  <ul className="submenu animate-slide-down">
                    {item.subItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.url;
                      return (
                        <li key={subItem.id}>
                          <Link to={subItem.url} onClick={() => setIsSidebarOpen(false)}>
                            <div className={`submenu-item ${isSubActive ? "submenu-active" : ""}`}>
                              {subItem.name}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {/* Sidebar Footer Logout */}
        <div className="sidebar-footer">
          <ul className="footer-menu">
            <li
              className="logout-item"
              onClick={handleLogout}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleLogout();
                }
              }}
            >
              <LogoutIcon />
              <span>Logout</span>
            </li>
          </ul>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="main">
        {shouldShowBackButton(location.pathname) && (
          <div className="back-btn-container">
            <button onClick={() => navigate(-1)} className="back-btn" aria-label="Go back">
              <BackIcon />
              <span>Back</span>
            </button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export const NavBar: React.FC<NavBarProps> = ({ children }) => {
  return (
    <RenderComponent>
      <NavBarComponent>{children}</NavBarComponent>
    </RenderComponent>
  );
};